import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import type { KeyboardEvent, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  FileIcon,
  XIcon,
} from "lucide-react";
import { DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checbox";
import { cn } from "@/utils/cn";
import {
  MIN_DOCUMENT_YEAR,
  getMaxDocumentYear,
  isDocumentDetailsValid,
  validateDocumentYear,
} from "@/utils/document-upload-queue";
import type { DocumentDetails } from "@/utils/document-upload-queue";

export type EditableFile = {
  id: string;
  file: File;
  details: DocumentDetails;
};

export type EditFileDetailsViewHandle = {
  /** Escape closes the innermost layer first: the confirmation, then the view. */
  handleEscape: () => void;
};

type Props = {
  files: EditableFile[];
  formatMeta: (file: File) => string;
  onSave: (details: Record<string, DocumentDetails>) => void;
  onCancel: () => void;
};

const fieldClassName =
  "bg-muted placeholder:text-muted-foreground focus-visible:ring-ring/50 focus-visible:border-ring aria-invalid:border-destructive h-12 w-full rounded-lg border px-3 text-sm outline-none focus-visible:ring-[3px] disabled:opacity-50";

type SharedDetails = Pick<DocumentDetails, "author" | "year" | "tags">;

const pickShared = ({ author, year, tags }: DocumentDetails): SharedDetails => ({
  author,
  year,
  tags,
});

/** Copies `shared` onto every draft except `sourceId`. */
const withSharedDetails = (
  drafts: Record<string, DocumentDetails>,
  sourceId: string,
  shared: Partial<SharedDetails>,
) =>
  Object.fromEntries(
    Object.entries(drafts).map(([id, details]) => [
      id,
      id === sourceId ? details : { ...details, ...shared },
    ]),
  );

const EditFileDetailsView = forwardRef<EditFileDetailsViewHandle, Props>(
  ({ files, formatMeta, onSave, onCancel }, ref) => {
    const { t, i18n } = useTranslation();
    const isRTL = i18n.dir() === "rtl";
    // Drafts for every file, so switching files keeps unsaved edits.
    const [drafts, setDrafts] = useState<Record<string, DocumentDetails>>(() =>
      Object.fromEntries(files.map((item) => [item.id, item.details])),
    );
    const [selectedId, setSelectedId] = useState(files[0]?.id);
    // The file whose author/year/tags are mirrored onto the others while checked.
    const [applySourceId, setApplySourceId] = useState<string | null>(null);
    // The others' details before applying, restored when the box is unchecked.
    const beforeApplyRef = useRef<Record<string, SharedDetails>>({});
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);
    const [tagInput, setTagInput] = useState("");
    const confirmApplyRef = useRef<HTMLButtonElement>(null);

    useImperativeHandle(ref, () => ({
      handleEscape: () => {
        if (isConfirmOpen) setIsConfirmOpen(false);
        else onCancel();
      },
    }));

    useEffect(() => {
      if (isConfirmOpen) confirmApplyRef.current?.focus();
    }, [isConfirmOpen]);

    const selected = files.find((item) => item.id === selectedId) ?? files[0];
    if (!selected) return null;
    const draft = drafts[selected.id];
    const otherCount = files.length - 1;

    const updateDraft = (patch: Partial<DocumentDetails>) =>
      setDrafts((current) => {
        const next = {
          ...current,
          [selected.id]: { ...current[selected.id], ...patch },
        };
        if (applySourceId !== selected.id) return next;
        const { title: _title, ...shared } = patch;
        return Object.keys(shared).length
          ? withSharedDetails(next, selected.id, shared)
          : next;
      });

    const selectFile = (id: string) => {
      setSelectedId(id);
      setTagInput("");
    };

    const addTag = () => {
      const tag = tagInput.trim().replace(/,$/, "").trim();
      if (tag && !draft.tags.includes(tag)) {
        updateDraft({ tags: [...draft.tags, tag] });
      }
      setTagInput("");
    };

    const handleTagKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "Enter" || event.key === ",") {
        event.preventDefault();
        addTag();
      } else if (event.key === "Backspace" && !tagInput && draft.tags.length) {
        updateDraft({ tags: draft.tags.slice(0, -1) });
      }
    };

    const handleApplyToggle = (checked: boolean) => {
      if (checked) {
        setIsConfirmOpen(true);
      } else {
        const before = beforeApplyRef.current;
        setDrafts((current) =>
          Object.fromEntries(
            Object.entries(current).map(([id, details]) => [
              id,
              before[id] ? { ...details, ...before[id] } : details,
            ]),
          ),
        );
        setApplySourceId(null);
      }
    };

    const confirmApply = () => {
      beforeApplyRef.current = Object.fromEntries(
        Object.entries(drafts)
          .filter(([id]) => id !== selected.id)
          .map(([id, details]) => [id, pickShared(details)]),
      );
      setDrafts((current) =>
        withSharedDetails(current, selected.id, pickShared(current[selected.id])),
      );
      setApplySourceId(selected.id);
      setIsConfirmOpen(false);
    };

    const handleSave = () => {
      const pendingTag = tagInput.trim();
      let next = drafts;
      if (pendingTag && !draft.tags.includes(pendingTag)) {
        const tags = [...draft.tags, pendingTag];
        next = { ...drafts, [selected.id]: { ...draft, tags } };
        if (applySourceId === selected.id) {
          next = withSharedDetails(next, selected.id, { tags });
        }
      }

      // Jump to the first file that still needs fixing instead of saving.
      const invalid = files.find((item) => !isDocumentDetailsValid(next[item.id]));
      if (invalid) {
        setDrafts(next);
        selectFile(invalid.id);
        return;
      }
      onSave(next);
    };

    const titleError = draft.title.trim()
      ? ""
      : t("collections.multiUpload.edit.titleRequired");
    const yearProblem = validateDocumentYear(draft.year);
    const yearError =
      yearProblem === "notInteger"
        ? t("collections.yearMustBeInteger")
        : yearProblem === "tooSmall"
          ? t("collections.numberFieldMin", {
              field: t("collections.year"),
              min: MIN_DOCUMENT_YEAR,
            })
          : yearProblem === "tooLarge"
            ? t("collections.numberFieldMax", {
                field: t("collections.year"),
                max: getMaxDocumentYear(),
              })
            : "";

    const BackIcon = isRTL ? ArrowRightIcon : ArrowLeftIcon;
    const RowChevron = isRTL ? ChevronLeftIcon : ChevronRightIcon;

    return (
      <div className="grid h-[min(680px,90vh)] min-h-0 grid-rows-[auto_minmax(0,1fr)] sm:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] sm:grid-rows-1">
        {/* File switcher */}
        <aside className="bg-muted/60 flex min-h-0 flex-col gap-3 border-b p-4 sm:border-e sm:border-b-0">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onCancel}
            className="self-end"
          >
            <BackIcon />
            {t("collections.multiUpload.edit.back")}
          </Button>
          <ul className="flex max-h-40 min-h-0 flex-col gap-2 overflow-y-auto sm:max-h-none sm:flex-1">
            {files.map((item) => {
              const isSelected = item.id === selected.id;
              const isValid = isDocumentDetailsValid(drafts[item.id]);
              return (
                <li key={item.id}>
                  <button
                    type="button"
                    onClick={() => selectFile(item.id)}
                    aria-current={isSelected ? "true" : undefined}
                    className={cn(
                      "bg-card hover:bg-accent focus-visible:ring-ring/50 flex w-full items-center gap-3 rounded-lg border px-3 py-2.5 text-start outline-none focus-visible:ring-[3px]",
                      isSelected && "border-primary bg-primary/10 hover:bg-primary/15",
                      !isValid && "border-destructive",
                    )}
                  >
                    <FileIcon
                      className="text-muted-foreground size-5 shrink-0"
                      strokeWidth={1.5}
                      aria-hidden
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium">
                        {item.file.name}
                      </span>
                      <span className="text-muted-foreground block truncate text-xs">
                        {formatMeta(item.file)}
                      </span>
                    </span>
                    <RowChevron
                      className="text-muted-foreground size-4 shrink-0"
                      aria-hidden
                    />
                  </button>
                </li>
              );
            })}
          </ul>
          <p className="text-muted-foreground hidden text-xs sm:block">
            {t("collections.multiUpload.edit.switchHint")}
          </p>
        </aside>

        {/* Form for the selected file */}
        <section className="relative flex min-h-0 flex-col">
          <header className="flex items-center justify-between gap-2 px-6 pt-5 pb-3">
            <DialogTitle>{t("collections.multiUpload.edit.title")}</DialogTitle>
            <DialogDescription className="sr-only">
              {t("collections.multiUpload.edit.switchHint")}
            </DialogDescription>
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              onClick={onCancel}
              aria-label={t("common.close")}
            >
              <XIcon />
            </Button>
          </header>

          <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto px-6 pb-4">
            <div className="bg-muted rounded-lg px-4 py-3">
              <p className="truncate text-sm font-medium">{selected.file.name}</p>
              <p className="text-muted-foreground text-xs">
                {formatMeta(selected.file)}
              </p>
            </div>

            <Field
              id="file-details-title"
              label={t("collections.multiUpload.edit.titleLabel")}
              hint={t("collections.multiUpload.edit.titleHint")}
              error={titleError}
            >
              <input
                id="file-details-title"
                className={fieldClassName}
                value={draft.title}
                onChange={(event) => updateDraft({ title: event.target.value })}
                aria-invalid={Boolean(titleError)}
                aria-describedby="file-details-title-hint"
                required
              />
            </Field>

            <Field
              id="file-details-author"
              label={t("collections.multiUpload.edit.authorLabel")}
            >
              <input
                id="file-details-author"
                className={fieldClassName}
                value={draft.author}
                placeholder={t("collections.multiUpload.edit.authorPlaceholder")}
                onChange={(event) => updateDraft({ author: event.target.value })}
              />
            </Field>

            <Field
              id="file-details-year"
              label={t("collections.multiUpload.edit.yearLabel")}
              hint={t("collections.yearRangeHint", {
                min: MIN_DOCUMENT_YEAR,
                max: getMaxDocumentYear(),
              })}
              error={yearError}
            >
              <input
                id="file-details-year"
                className={fieldClassName}
                inputMode="numeric"
                value={draft.year}
                placeholder={String(new Date().getFullYear())}
                onChange={(event) => {
                  const nextYear = event.target.value;
                  if (/^\d{0,4}$/.test(nextYear)) updateDraft({ year: nextYear });
                }}
                aria-invalid={Boolean(yearError)}
                aria-describedby="file-details-year-hint"
              />
            </Field>

            <Field
              id="file-details-tags"
              label={t("collections.multiUpload.edit.tagsLabel")}
            >
              <div className="bg-muted focus-within:ring-ring/50 flex min-h-12 flex-wrap items-center gap-2 rounded-lg border px-3 py-2 focus-within:ring-[3px]">
                {draft.tags.map((tag) => (
                  <TagChip
                    key={tag}
                    tag={tag}
                    removeLabel={t("collections.multiUpload.edit.removeTag", { tag })}
                    onRemove={() =>
                      updateDraft({ tags: draft.tags.filter((item) => item !== tag) })
                    }
                  />
                ))}
                <input
                  id="file-details-tags"
                  className="placeholder:text-muted-foreground min-w-24 flex-1 bg-transparent px-1 text-sm outline-none"
                  value={tagInput}
                  placeholder={
                    draft.tags.length
                      ? ""
                      : t("collections.multiUpload.edit.tagsPlaceholder")
                  }
                  onChange={(event) => setTagInput(event.target.value)}
                  onKeyDown={handleTagKeyDown}
                  onBlur={addTag}
                />
              </div>
            </Field>

            {otherCount > 0 && (
              <div className="flex flex-col gap-1">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="file-details-apply"
                    checked={applySourceId === selected.id}
                    onCheckedChange={(checked) => handleApplyToggle(checked === true)}
                    className="border-muted-foreground dark:bg-transparent"
                  />
                  <label htmlFor="file-details-apply" className="text-sm">
                    {t("collections.multiUpload.edit.applyToOthers", {
                      count: otherCount,
                    })}
                  </label>
                </div>
                <p className="text-muted-foreground ps-6 text-xs">
                  {t("collections.multiUpload.edit.applyToOthersHint")}
                </p>
              </div>
            )}
          </div>

          <footer className="flex justify-end gap-2 border-t px-6 py-4">
            <Button type="button" variant="outline" onClick={onCancel}>
              {t("common.cancel")}
            </Button>
            <Button type="button" onClick={handleSave}>
              {t("collections.multiUpload.edit.save")}
            </Button>
          </footer>

          {isConfirmOpen && (
            <div className="absolute inset-0 z-10 flex items-end justify-center bg-black/40 p-4 sm:items-center">
              <div
                role="alertdialog"
                aria-modal="true"
                aria-labelledby="file-details-confirm-title"
                aria-describedby="file-details-confirm-description"
                className="bg-popover text-popover-foreground flex w-full max-w-sm flex-col gap-4 rounded-xl border p-5 shadow-lg"
              >
                <div className="flex flex-col gap-1">
                  <h3 id="file-details-confirm-title" className="font-semibold">
                    {t("collections.multiUpload.edit.confirmTitle", {
                      count: otherCount,
                    })}
                  </h3>
                  <p
                    id="file-details-confirm-description"
                    className="text-muted-foreground text-xs"
                  >
                    {t("collections.multiUpload.edit.confirmDescription")}
                  </p>
                </div>
                <div className="bg-card rounded-lg border p-4">
                  <p className="text-muted-foreground border-b pb-2 text-xs">
                    {t("collections.multiUpload.edit.confirmDetails")}
                  </p>
                  <dl className="grid grid-cols-[auto_1fr] items-center gap-x-6 gap-y-2 pt-3 text-sm">
                    <dt>{t("collections.author")}</dt>
                    <dd className="text-muted-foreground truncate">
                      {draft.author.trim() || "—"}
                    </dd>
                    <dt>{t("collections.year")}</dt>
                    <dd className="text-muted-foreground">{draft.year || "—"}</dd>
                    <dt>{t("collections.multiUpload.edit.tags")}</dt>
                    <dd className="flex flex-wrap gap-1">
                      {draft.tags.length
                        ? draft.tags.map((tag) => <TagChip key={tag} tag={tag} />)
                        : "—"}
                    </dd>
                  </dl>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsConfirmOpen(false)}
                  >
                    {t("common.cancel")}
                  </Button>
                  <Button
                    ref={confirmApplyRef}
                    type="button"
                    onClick={confirmApply}
                  >
                    {t("collections.multiUpload.edit.confirmApply", {
                      count: otherCount,
                    })}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </section>
      </div>
    );
  },
);
EditFileDetailsView.displayName = "EditFileDetailsView";

type TagChipProps = {
  tag: string;
  removeLabel?: string;
  onRemove?: () => void;
};

const TagChip = ({ tag, removeLabel, onRemove }: TagChipProps) => (
  <span className="bg-foreground text-background inline-flex max-w-full items-center gap-1.5 rounded-lg px-2.5 py-1 text-sm leading-none font-medium">
    <span className="truncate">{tag}</span>
    {onRemove && (
      <button
        type="button"
        onClick={onRemove}
        aria-label={removeLabel}
        className="-me-0.5 rounded-sm opacity-80 hover:opacity-100 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-current"
      >
        <XIcon className="size-3.5" aria-hidden />
      </button>
    )}
  </span>
);

type FieldProps = {
  id: string;
  label: string;
  hint?: string;
  error?: string;
  children: ReactNode;
};

const Field = ({ id, label, hint, error, children }: FieldProps) => (
  <div className="flex flex-col gap-1.5">
    <label htmlFor={id} className="text-sm font-medium">
      {label}
    </label>
    {children}
    {(error || hint) && (
      <p
        id={`${id}-hint`}
        className={cn("text-xs", error ? "text-destructive" : "text-muted-foreground")}
      >
        {error || hint}
      </p>
    )}
  </div>
);

export default EditFileDetailsView;
